import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { aklioLegalDocuments } from './aklioLegalDocuments';
import { useParallaxMotion } from './useParallaxMotion';

const appUrl = 'https://aklio.thebluecoding.com/';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:ital,wght@0,300;0,400;0,500&family=Syne:wght@400;700;800&display=swap');

  .bmx-root *, .bmx-root *::before, .bmx-root *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .bmx-root {
    --bmx-bg: #f7f9ff;
    --bmx-ink: #101315;
    --bmx-muted: #5f6b7a;
    --bmx-blue: #1a3cff;
    --bmx-cyan: #009dff;
    --bmx-blue-soft: #edf2ff;
    --bmx-amber: #f7c66a;
    --bmx-line: rgba(16, 19, 21, 0.12);
    --bmx-dark: #101315;
    --bmx-mono: 'DM Mono', monospace;
    --bmx-sans: 'Syne', sans-serif;
    --motion-y-soft: 0px;
    --motion-y-mid: 0px;
    --motion-y-deep: 0px;
    --motion-y-forward: 0px;
    --motion-pointer-x: 0px;
    --motion-pointer-y: 0px;
    --motion-pointer-x-invert: 0px;
    --motion-pointer-y-invert: 0px;
    --motion-rotate-soft: 0deg;
    --motion-tilt: 0deg;
    min-height: 100vh;
    background: var(--bmx-bg);
    color: var(--bmx-ink);
    font-family: var(--bmx-sans);
    overflow-x: hidden;
  }

  .bmx-nav {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.25rem;
    padding: 1.2rem 3rem;
    background: rgba(247, 248, 245, 0.9);
    backdrop-filter: blur(14px);
    border-bottom: 1px solid var(--bmx-line);
  }

  .bmx-brand,
  .bmx-logo {
    color: var(--bmx-ink);
    text-decoration: none;
  }

  .bmx-logo {
    display: block;
    font-family: var(--bmx-mono);
    font-size: 0.82rem;
    font-weight: 500;
    letter-spacing: 0.04em;
  }

  .bmx-logo span { color: var(--bmx-blue); }

  .bmx-brand small {
    display: block;
    margin-top: 0.2rem;
    color: var(--bmx-muted);
    font-family: var(--bmx-mono);
    font-size: 0.66rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .bmx-nav-links {
    display: flex;
    align-items: center;
    gap: 2rem;
    list-style: none;
  }

  .bmx-nav-links a {
    color: var(--bmx-ink);
    font-family: var(--bmx-mono);
    font-size: 0.76rem;
    letter-spacing: 0.04em;
    opacity: 0.58;
    text-decoration: none;
    transition: opacity 0.2s, color 0.2s;
  }

  .bmx-nav-links a:hover {
    color: var(--bmx-blue);
    opacity: 1;
  }

  .bmx-nav-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .bmx-btn,
  .bmx-link-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 42px;
    padding: 0.65rem 1.15rem;
    border: 1px solid var(--bmx-blue);
    color: #fff;
    background: var(--bmx-blue);
    font-family: var(--bmx-mono);
    font-size: 0.76rem;
    letter-spacing: 0.04em;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    transition: transform 0.2s, background 0.2s, border-color 0.2s, color 0.2s;
  }

  .bmx-btn:hover,
  .bmx-link-btn:hover {
    background: #0026cc;
    border-color: #0026cc;
    transform: translateY(-1px);
  }

  .bmx-btn-secondary {
    color: var(--bmx-blue);
    background: transparent;
  }

  .bmx-btn-secondary:hover {
    color: #fff;
    background: var(--bmx-blue);
    border-color: var(--bmx-blue);
  }

  .bmx-hero {
    position: relative;
    min-height: 88vh;
    padding: 8rem 3rem 4rem;
    overflow: hidden;
    background: linear-gradient(135deg, #f7f9ff 0%, #edf2ff 100%);
    border-bottom: 1px solid var(--bmx-line);
  }

  .bmx-hero-scene {
    position: absolute;
    inset: 5.5rem 0 0 0;
    pointer-events: none;
    overflow: hidden;
  }

  .bmx-hero-layer {
    position: absolute;
    display: block;
    border: 1px solid rgba(26,60,255,0.14);
    will-change: transform;
  }

  .bmx-hero-layer-grid {
    left: 4vw;
    top: 5rem;
    width: min(28vw, 320px);
    min-width: 220px;
    aspect-ratio: 1;
    background-image:
      linear-gradient(rgba(0,157,255,0.16) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,157,255,0.16) 1px, transparent 1px);
    background-size: 30px 30px;
    transform: translate3d(var(--motion-pointer-x), var(--motion-y-mid), 0) rotate(9deg);
  }

  .bmx-hero-layer-card {
    right: 13vw;
    bottom: 7vh;
    width: 13rem;
    height: 6.5rem;
    background:
      linear-gradient(90deg, rgba(255,255,255,0.78) 0 26%, transparent 26%),
      rgba(255,255,255,0.46);
    box-shadow: 0 22px 54px rgba(16, 19, 21, 0.08);
    transform: translate3d(var(--motion-pointer-x-invert), var(--motion-y-forward), 0) rotate(-8deg);
  }

  .bmx-hero-layer-rail {
    left: 38vw;
    bottom: 13vh;
    width: min(34vw, 520px);
    height: 1px;
    border: 0;
    background: rgba(16, 19, 21, 0.22);
    transform: translate3d(var(--motion-pointer-x), var(--motion-y-soft), 0);
  }

  .bmx-dashboard {
    position: absolute;
    z-index: 2;
    right: -3rem;
    top: 2rem;
    width: min(760px, 58vw);
    min-height: 540px;
    padding: 1.2rem;
    background: rgba(255,255,255,0.82);
    border: 1px solid rgba(16,19,21,0.14);
    box-shadow: 0 30px 80px rgba(16, 19, 21, 0.14);
    transform: translate3d(var(--motion-pointer-x-invert), var(--motion-y-soft), 0) rotate(calc(-2deg + var(--motion-tilt)));
    will-change: transform;
  }

  .bmx-dashboard-top {
    display: grid;
    grid-template-columns: 1.2fr 0.8fr;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .bmx-panel {
    border: 1px solid rgba(16,19,21,0.1);
    background: #fff;
    padding: 1rem;
    min-height: 130px;
  }

  .bmx-panel-title {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
    color: var(--bmx-muted);
    font-family: var(--bmx-mono);
    font-size: 0.68rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .bmx-metric-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.7rem;
  }

  .bmx-metric {
    min-height: 88px;
    padding: 0.75rem;
    background: #f6f9fb;
    border-left: 3px solid var(--bmx-blue);
  }

  .bmx-metric strong {
    display: block;
    margin-bottom: 0.4rem;
    color: var(--bmx-ink);
    font-size: 1.65rem;
    line-height: 1;
  }

  .bmx-metric span,
  .bmx-appointment span,
  .bmx-pill {
    color: var(--bmx-muted);
    font-family: var(--bmx-mono);
    font-size: 0.66rem;
    letter-spacing: 0.04em;
  }

  .bmx-chart {
    display: flex;
    align-items: end;
    gap: 0.48rem;
    min-height: 112px;
  }

  .bmx-chart span {
    flex: 1;
    min-height: 28px;
    background: var(--bmx-blue-soft);
    border-top: 3px solid var(--bmx-cyan);
  }

  .bmx-chart span:nth-child(2) { height: 76px; }
  .bmx-chart span:nth-child(3) { height: 54px; }
  .bmx-chart span:nth-child(4) { height: 94px; border-top-color: var(--bmx-blue); }
  .bmx-chart span:nth-child(5) { height: 64px; }
  .bmx-chart span:nth-child(6) { height: 104px; border-top-color: var(--bmx-amber); }

  .bmx-appointment-list {
    display: grid;
    gap: 0.75rem;
  }

  .bmx-appointment {
    display: grid;
    grid-template-columns: 64px 1fr auto;
    align-items: center;
    gap: 0.8rem;
    min-height: 62px;
    padding: 0.75rem;
    background: #f7f9ff;
    border: 1px solid rgba(16,19,21,0.08);
  }

  .bmx-appointment strong {
    display: block;
    margin-bottom: 0.2rem;
    font-size: 0.92rem;
  }

  .bmx-pill {
    display: inline-flex;
    align-items: center;
    min-height: 26px;
    padding: 0 0.65rem;
    background: var(--bmx-blue-soft);
    color: #1637b8;
  }

  .bmx-hero-content {
    position: relative;
    z-index: 2;
    max-width: 720px;
    padding-top: 5rem;
  }

  .bmx-eyebrow,
  .bmx-section-label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--bmx-muted);
    font-family: var(--bmx-mono);
    font-size: 0.72rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .bmx-eyebrow::before,
  .bmx-section-label::after {
    content: '';
    display: inline-block;
    width: 2rem;
    height: 1px;
    background: var(--bmx-blue);
  }

  .bmx-title {
    margin-top: 1.7rem;
    max-width: 9ch;
    font-size: 5.8rem;
    font-weight: 800;
    line-height: 0.95;
  }

  .bmx-title span { color: var(--bmx-blue); }

  .bmx-hero-copy {
    max-width: 48ch;
    margin-top: 1.8rem;
    color: #3f4b4f;
    font-family: var(--bmx-mono);
    font-size: 0.92rem;
    line-height: 1.9;
  }

  .bmx-hero-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.85rem;
    margin-top: 2rem;
  }

  .bmx-quick-strip {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    border-bottom: 1px solid var(--bmx-line);
    background: var(--bmx-dark);
    color: #fff;
  }

  .bmx-quick-item {
    min-height: 108px;
    padding: 1.5rem 1.6rem;
    border-right: 1px solid rgba(255,255,255,0.12);
  }

  .bmx-quick-item:last-child { border-right: none; }

  .bmx-quick-item strong {
    display: block;
    margin-bottom: 0.5rem;
    color: var(--bmx-amber);
    font-size: 1.7rem;
    line-height: 1;
  }

  .bmx-quick-item span {
    color: rgba(255,255,255,0.62);
    font-family: var(--bmx-mono);
    font-size: 0.72rem;
    line-height: 1.6;
  }

  .bmx-section {
    padding: 6.5rem 3rem;
  }

  .bmx-section-header {
    display: grid;
    grid-template-columns: 0.82fr 1fr;
    align-items: end;
    gap: 3rem;
    margin-bottom: 3.5rem;
  }

  .bmx-section-title {
    margin-top: 1.5rem;
    font-size: 3.7rem;
    line-height: 1;
    font-weight: 800;
  }

  .bmx-section-copy {
    max-width: 58ch;
    color: var(--bmx-muted);
    font-family: var(--bmx-mono);
    font-size: 0.86rem;
    line-height: 1.9;
  }

  .bmx-modules-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    border-top: 1px solid var(--bmx-line);
    border-left: 1px solid var(--bmx-line);
  }

  .bmx-module {
    min-height: 245px;
    padding: 2rem;
    background: #fff;
    border-right: 1px solid var(--bmx-line);
    border-bottom: 1px solid var(--bmx-line);
    transition: background 0.2s, transform 0.2s;
  }

  .bmx-module:hover {
    background: #eef6ff;
    transform: translateY(-2px);
  }

  .bmx-module-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    margin-bottom: 1.6rem;
    background: var(--bmx-dark);
    color: #fff;
    font-family: var(--bmx-mono);
    font-size: 0.72rem;
  }

  .bmx-module h3 {
    margin-bottom: 1rem;
    font-size: 1.28rem;
    line-height: 1.25;
  }

  .bmx-module p {
    color: var(--bmx-muted);
    font-family: var(--bmx-mono);
    font-size: 0.78rem;
    line-height: 1.85;
  }

  .bmx-benefits {
    background: #fff;
    border-top: 1px solid var(--bmx-line);
    border-bottom: 1px solid var(--bmx-line);
  }

  .bmx-benefit-grid {
    display: grid;
    grid-template-columns: 0.9fr 1.1fr;
    gap: 5rem;
    align-items: center;
  }

  .bmx-benefit-list {
    display: grid;
    gap: 1rem;
  }

  .bmx-benefit {
    display: grid;
    grid-template-columns: 46px 1fr;
    gap: 1rem;
    align-items: start;
    padding: 1.2rem 0;
    border-bottom: 1px solid var(--bmx-line);
  }

  .bmx-benefit:last-child { border-bottom: none; }

  .bmx-benefit span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    background: var(--bmx-blue-soft);
    color: #1637b8;
    font-family: var(--bmx-mono);
    font-size: 0.76rem;
  }

  .bmx-benefit h3 {
    margin-bottom: 0.55rem;
    font-size: 1.15rem;
  }

  .bmx-benefit p {
    color: var(--bmx-muted);
    font-family: var(--bmx-mono);
    font-size: 0.78rem;
    line-height: 1.8;
  }

  .bmx-record-preview {
    position: relative;
    min-height: 520px;
    padding: 1.2rem;
    background: #f7f9ff;
    border: 1px solid var(--bmx-line);
    overflow: hidden;
  }

  .bmx-record-preview::before {
    content: '';
    position: absolute;
    inset: -22%;
    background-image:
      linear-gradient(rgba(26,60,255,0.06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(26,60,255,0.06) 1px, transparent 1px);
    background-size: 36px 36px;
    pointer-events: none;
    transform: translate3d(var(--motion-pointer-x-invert), var(--motion-y-soft), 0) rotate(-3deg);
    will-change: transform;
  }

  .bmx-record-header {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--bmx-line);
  }

  .bmx-record-header strong {
    font-size: 1.1rem;
  }

  .bmx-record-body {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 0.72fr 1.28fr;
    gap: 1rem;
    padding-top: 1rem;
  }

  .bmx-timeline {
    display: grid;
    gap: 0.8rem;
  }

  .bmx-timeline-item {
    padding: 1rem;
    background: #fff;
    border-left: 3px solid var(--bmx-blue);
  }

  .bmx-timeline-item:nth-child(2) { border-left-color: var(--bmx-cyan); }
  .bmx-timeline-item:nth-child(3) { border-left-color: var(--bmx-amber); }

  .bmx-timeline-item strong {
    display: block;
    margin-bottom: 0.35rem;
    font-size: 0.92rem;
  }

  .bmx-timeline-item span {
    color: var(--bmx-muted);
    font-family: var(--bmx-mono);
    font-size: 0.66rem;
  }

  .bmx-notes {
    min-height: 360px;
    padding: 1.2rem;
    background: #fff;
  }

  .bmx-note-line {
    height: 12px;
    margin-bottom: 1rem;
    background: #e4e9ec;
  }

  .bmx-note-line:nth-child(1) { width: 92%; }
  .bmx-note-line:nth-child(2) { width: 75%; }
  .bmx-note-line:nth-child(3) { width: 84%; }
  .bmx-note-line:nth-child(4) { width: 58%; margin-bottom: 2rem; }

  .bmx-badge-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    margin-top: 1rem;
  }

  .bmx-dark-section {
    position: relative;
    background: var(--bmx-dark);
    color: #fff;
    overflow: hidden;
  }

  .bmx-dark-section::before {
    content: '';
    position: absolute;
    right: -10rem;
    top: 5rem;
    width: 36rem;
    height: 36rem;
    border: 1px solid rgba(247,198,106,0.24);
    background-image: repeating-linear-gradient(
      90deg,
      rgba(247,198,106,0.13) 0 1px,
      transparent 1px 28px
    );
    pointer-events: none;
    transform: translate3d(var(--motion-pointer-x-invert), var(--motion-y-soft), 0) rotate(-10deg);
    will-change: transform;
  }

  .bmx-dark-section > * {
    position: relative;
    z-index: 1;
  }

  .bmx-dark-section .bmx-section-copy,
  .bmx-dark-section .bmx-section-label {
    color: rgba(255,255,255,0.6);
  }

  .bmx-plan-audience {
    display: inline-grid;
    grid-template-columns: repeat(2, minmax(190px, 1fr));
    gap: 0;
    margin: 0 0 1.25rem;
    border: 1px solid rgba(255,255,255,0.16);
    background: rgba(255,255,255,0.04);
  }

  .bmx-plan-audience button {
    min-height: 54px;
    padding: 0.85rem 1.2rem;
    border: 0;
    border-right: 1px solid rgba(255,255,255,0.16);
    background: transparent;
    color: rgba(255,255,255,0.58);
    cursor: pointer;
    font-family: var(--bmx-mono);
    font-size: 0.74rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    transition: background 0.2s, color 0.2s;
  }

  .bmx-plan-audience button:last-child {
    border-right: none;
  }

  .bmx-plan-audience button:hover,
  .bmx-plan-audience button:focus-visible,
  .bmx-plan-audience button.bmx-plan-audience-active {
    background: #fff;
    color: var(--bmx-ink);
    outline: none;
  }

  .bmx-plan-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
  }

  .bmx-plan-grid-clinicas {
    grid-template-columns: minmax(0, 1fr);
  }

  .bmx-plan {
    display: flex;
    flex-direction: column;
    min-height: 520px;
    padding: 2rem;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.12);
    transition: transform 0.2s, border-color 0.2s, background 0.2s;
  }

  .bmx-plan:hover {
    transform: translateY(-3px);
    border-color: rgba(255,255,255,0.28);
    background: rgba(255,255,255,0.07);
  }

  .bmx-plan-featured {
    background: #fff;
    color: var(--bmx-ink);
    border-color: #fff;
  }

  .bmx-plan-featured:hover {
    background: #fff;
    border-color: #fff;
  }

  .bmx-plan-kicker {
    color: var(--bmx-amber);
    font-family: var(--bmx-mono);
    font-size: 0.7rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .bmx-plan-featured .bmx-plan-kicker { color: var(--bmx-blue); }

  .bmx-plan h3 {
    margin-top: 1rem;
    font-size: 1.7rem;
  }

  .bmx-plan-price {
    margin: 1.4rem 0 1rem;
    font-size: 2.4rem;
    font-weight: 800;
    line-height: 1;
  }

  .bmx-plan-price span {
    display: block;
    margin-top: 0.45rem;
    color: rgba(255,255,255,0.48);
    font-family: var(--bmx-mono);
    font-size: 0.72rem;
    font-weight: 400;
    line-height: 1.5;
  }

  .bmx-plan-featured .bmx-plan-price span {
    color: var(--bmx-muted);
  }

  .bmx-plan-desc {
    min-height: 74px;
    color: rgba(255,255,255,0.62);
    font-family: var(--bmx-mono);
    font-size: 0.78rem;
    line-height: 1.8;
  }

  .bmx-plan-featured .bmx-plan-desc { color: var(--bmx-muted); }

  .bmx-plan ul {
    display: grid;
    gap: 0.75rem;
    margin: 1.8rem 0 2rem;
    padding: 0;
    list-style: none;
  }

  .bmx-plan li {
    position: relative;
    padding-left: 1.25rem;
    color: rgba(255,255,255,0.72);
    font-family: var(--bmx-mono);
    font-size: 0.76rem;
    line-height: 1.7;
  }

  .bmx-plan-featured li { color: #47545a; }

  .bmx-plan li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.7rem;
    width: 6px;
    height: 6px;
    background: var(--bmx-cyan);
  }

  .bmx-plan-note {
    display: block;
    margin-top: auto;
    padding: 1rem 0 1.4rem;
    color: rgba(255,255,255,0.62);
    font-family: var(--bmx-mono);
    font-size: 0.72rem;
    line-height: 1.6;
  }

  .bmx-plan-featured .bmx-plan-note {
    color: var(--bmx-muted);
  }

  .bmx-plan .bmx-link-btn {
    margin-top: auto;
    width: 100%;
  }

  .bmx-plan:not(.bmx-plan-featured) .bmx-link-btn {
    border-color: rgba(255,255,255,0.22);
    background: transparent;
  }

  .bmx-plan:not(.bmx-plan-featured) .bmx-link-btn:hover {
    border-color: var(--bmx-blue);
    background: var(--bmx-blue);
  }

  .bmx-legal-band {
    background: #fff;
    border-top: 1px solid var(--bmx-line);
    border-bottom: 1px solid var(--bmx-line);
  }

  .bmx-legal-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    border-top: 1px solid var(--bmx-line);
    border-left: 1px solid var(--bmx-line);
  }

  .bmx-legal-card {
    display: flex;
    flex-direction: column;
    min-height: 250px;
    padding: 1.5rem;
    background: #fff;
    border-right: 1px solid var(--bmx-line);
    border-bottom: 1px solid var(--bmx-line);
    color: var(--bmx-ink);
    text-decoration: none;
    transition: background 0.2s, transform 0.2s;
  }

  .bmx-legal-card:hover {
    background: #eef6ff;
    transform: translateY(-2px);
  }

  .bmx-legal-card span {
    color: var(--bmx-blue);
    font-family: var(--bmx-mono);
    font-size: 0.68rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .bmx-legal-card h3 {
    margin-top: 1.4rem;
    font-size: 1.12rem;
    line-height: 1.3;
  }

  .bmx-legal-card p {
    margin-top: 0.9rem;
    color: var(--bmx-muted);
    font-family: var(--bmx-mono);
    font-size: 0.72rem;
    line-height: 1.75;
  }

  .bmx-legal-card strong {
    margin-top: auto;
    padding-top: 1.25rem;
    color: var(--bmx-blue);
    font-family: var(--bmx-mono);
    font-size: 0.72rem;
    font-weight: 500;
  }

  .bmx-cta {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 2rem;
    background: linear-gradient(135deg, var(--bmx-dark) 0%, #16286f 100%);
    color: #fff;
    border-top: 1px solid var(--bmx-line);
  }

  .bmx-cta h2 {
    max-width: 14ch;
    font-size: 3.3rem;
    line-height: 1;
  }

  .bmx-cta p {
    max-width: 54ch;
    margin-top: 1rem;
    color: rgba(255,255,255,0.68);
    font-family: var(--bmx-mono);
    font-size: 0.84rem;
    line-height: 1.8;
  }

  .bmx-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 2.2rem 3rem;
    background: var(--bmx-dark);
    color: #fff;
  }

  .bmx-footer .bmx-logo { color: #fff; }

  .bmx-footer span {
    color: rgba(255,255,255,0.42);
    font-family: var(--bmx-mono);
    font-size: 0.7rem;
    letter-spacing: 0.06em;
  }

  @keyframes bmx-reveal {
    from {
      opacity: 0;
      transform: translateY(34px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes bmx-layer-pulse {
    0%, 100% { opacity: 0.72; }
    50% { opacity: 1; }
  }

  .bmx-hero-layer-grid,
  .bmx-hero-layer-card {
    animation: bmx-layer-pulse 7s ease-in-out infinite;
  }

  @supports (animation-timeline: view()) {
    .bmx-quick-item,
    .bmx-section-label,
    .bmx-section-title,
    .bmx-section-copy,
    .bmx-module,
    .bmx-benefit,
    .bmx-record-preview,
    .bmx-plan {
      animation: bmx-reveal linear both;
      animation-timeline: view();
      animation-range: entry 0% cover 28%;
    }

    .bmx-module:nth-child(2n),
    .bmx-plan:nth-child(2n) {
      animation-range: entry 6% cover 34%;
    }

    .bmx-legal-band .bmx-section-label,
    .bmx-legal-band .bmx-section-title,
    .bmx-legal-band .bmx-section-copy,
    .bmx-legal-card {
      animation: none;
      opacity: 1;
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .bmx-root *,
    .bmx-root *::before,
    .bmx-root *::after {
      animation-duration: 0.001ms !important;
      animation-iteration-count: 1 !important;
      scroll-behavior: auto !important;
      transition-duration: 0.001ms !important;
    }

    .bmx-dashboard,
    .bmx-hero-layer,
    .bmx-record-preview::before,
    .bmx-dark-section::before {
      transform: none !important;
    }
  }

  @media (max-width: 1100px) {
    .bmx-dashboard {
      right: -11rem;
      width: 720px;
      opacity: 0.72;
    }

    .bmx-section-header,
    .bmx-benefit-grid {
      grid-template-columns: 1fr;
      gap: 2.5rem;
    }

    .bmx-modules-grid,
    .bmx-legal-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .bmx-plan-grid {
      grid-template-columns: 1fr;
    }

    .bmx-quick-strip {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 820px) {
    .bmx-nav,
    .bmx-hero,
    .bmx-section,
    .bmx-cta,
    .bmx-footer {
      padding-left: 1.2rem;
      padding-right: 1.2rem;
    }

    .bmx-nav {
      align-items: flex-start;
      padding-top: 1rem;
      padding-bottom: 1rem;
    }

    .bmx-nav-links {
      display: none;
    }

    .bmx-nav-actions {
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    .bmx-nav-actions .bmx-btn-secondary {
      display: none;
    }

    .bmx-btn,
    .bmx-link-btn {
      min-height: 40px;
      padding: 0.6rem 0.85rem;
      font-size: 0.7rem;
    }

    .bmx-hero {
      min-height: 84vh;
      padding-top: 7rem;
      padding-bottom: 3rem;
    }

    .bmx-hero-content {
      padding-top: 2rem;
    }

    .bmx-title {
      max-width: 10ch;
      font-size: 3.4rem;
    }

    .bmx-dashboard {
      top: 12rem;
      right: -22rem;
      width: 620px;
      opacity: 0.36;
      transform: translate3d(var(--motion-pointer-x-invert), var(--motion-y-soft), 0) rotate(calc(-3deg + var(--motion-tilt)));
    }

    .bmx-hero-layer-grid {
      left: -5rem;
      opacity: 0.5;
    }

    .bmx-hero-layer-card,
    .bmx-hero-layer-rail {
      opacity: 0.42;
    }

    .bmx-quick-strip,
    .bmx-modules-grid,
    .bmx-legal-grid,
    .bmx-plan-grid,
    .bmx-record-body,
    .bmx-cta {
      grid-template-columns: 1fr;
    }

    .bmx-plan-audience {
      width: 100%;
      grid-template-columns: 1fr;
    }

    .bmx-plan-audience button {
      border-right: none;
      border-bottom: 1px solid rgba(255,255,255,0.16);
    }

    .bmx-plan-audience button:last-child {
      border-bottom: none;
    }

    .bmx-section {
      padding-top: 4.5rem;
      padding-bottom: 4.5rem;
    }

    .bmx-section-title,
    .bmx-cta h2 {
      font-size: 2.45rem;
    }

    .bmx-module,
    .bmx-plan {
      min-height: auto;
    }

    .bmx-record-preview {
      min-height: auto;
    }

    .bmx-footer {
      flex-direction: column;
      align-items: flex-start;
    }
  }
`;

const modules = [
  {
    title: 'Agenda medica inteligente',
    text: 'Organiza turnos por medico, especialidad, sede y estado de atencion para que recepcion trabaje con mayor claridad.',
  },
  {
    title: 'Pacientes centralizados',
    text: 'Conserva datos de contacto, historial de visitas, archivos y observaciones relevantes en un solo perfil.',
  },
  {
    title: 'Historia clinica digital',
    text: 'Registra antecedentes, signos vitales, diagnosticos, evolucion y notas medicas sin depender del papel.',
  },
  {
    title: 'Recetas y certificados',
    text: 'Genera documentos medicos listos para imprimir o compartir con el paciente desde la misma consulta.',
  },
  {
    title: 'Roles y permisos',
    text: 'Controla accesos para administradores, medicos, asistentes y personal operativo segun sus responsabilidades.',
  },
  {
    title: 'Indicadores y reportes',
    text: 'Visualiza actividad diaria, citas atendidas, pacientes recurrentes y datos clave para tomar decisiones.',
  },
];

const benefits = [
  {
    title: 'Menos tiempo administrativo',
    text: 'Reduce tareas repetitivas en recepcion y consulta con flujos claros para cada etapa de atencion.',
  },
  {
    title: 'Informacion ordenada',
    text: 'Cada paciente mantiene un expediente consistente, consultable y preparado para continuidad medica.',
  },
  {
    title: 'Mejor experiencia del paciente',
    text: 'Agiliza registros, citas, documentos y seguimiento para que la atencion se sienta mas profesional.',
  },
  {
    title: 'Sistema listo para crecer',
    text: 'AKLIO puede adaptarse a consultorios, equipos medicos y clinicas con varias sedes.',
  },
];

const consultorioPlans = [
  {
    kicker: 'light',
    name: 'Light',
    price: '$11.99',
    priceNote: 'inc. IVA',
    description: 'Para consultas privadas que estan empezando a digitalizar su atencion diaria.',
    items: [
      'Hasta 100 pacientes mensuales',
      '5 GB de espacio',
      'Agenda e historia clinica digital',
      'Recetas y certificados',
    ],
    note: 'Ideal para consultas independientes.',
  },
  {
    kicker: 'standard',
    name: 'Standard',
    price: '$13.99',
    priceNote: 'inc. IVA',
    description: 'Para consultorios privados con mayor volumen de pacientes y operacion constante.',
    items: [
      'Hasta 200 pacientes mensuales',
      '10 GB de espacio',
      'Agenda e historia clinica digital',
      'Reportes operativos',
    ],
    note: 'Buen balance entre capacidad y costo.',
    featured: true,
  },
  {
    kicker: 'advance',
    name: 'Advance',
    price: '$19.99',
    priceNote: 'inc. IVA',
    description: 'Para profesionales que necesitan crecer sin limite de atenciones y sumar IA a su flujo.',
    items: [
      'Atenciones ilimitadas',
      '25 GB de espacio',
      'Asistente de IA incluido',
      'IA hasta 50 pacientes mensuales',
    ],
    note: 'Pensado para operaciones privadas en crecimiento.',
  },
];

const clinicaPlans = [
  {
    kicker: 'clinicas',
    name: 'Clinica Escalable',
    price: 'Contactanos',
    priceNote: 'propuesta a medida',
    description: 'Para clinicas que necesitan sedes, flujos personalizados, integraciones y acompanamiento directo.',
    items: [
      'Configuracion por sedes, areas o especialidades',
      'Usuarios, roles y permisos por equipo',
      'Flujos y documentos clinicos personalizados',
      'Integraciones segun alcance del proyecto',
      'Soporte y mejoras planificadas',
    ],
    note: 'El precio se define despues de revisar el alcance real de la clinica.',
    featured: true,
  },
];

const AKLIOPage: React.FC = () => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);
  useParallaxMotion(rootRef);
  const [activePlanGroup, setActivePlanGroup] = useState<'consultorio' | 'clinicas'>('consultorio');
  const visiblePlans = activePlanGroup === 'consultorio' ? consultorioPlans : clinicaPlans;

  useEffect(() => {
    if (!document.getElementById('aklio-styles')) {
      const el = document.createElement('style');
      el.id = 'aklio-styles';
      el.textContent = styles;
      document.head.appendChild(el);
      styleRef.current = el;
    }

    return () => {
      styleRef.current?.remove();
    };
  }, []);

  return (
    <div className="bmx-root" ref={rootRef}>
      <nav className="bmx-nav">
        <Link to="/" className="bmx-brand">
          <span className="bmx-logo">
            AKLIO
          </span>
          <small>by thebluecoding</small>
        </Link>

        <ul className="bmx-nav-links">
          <li><a href="#modulos">modulos</a></li>
          <li><a href="#beneficios">beneficios</a></li>
          <li><a href="#planes">planes</a></li>
        </ul>

        <div className="bmx-nav-actions">
          <a
            href={appUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bmx-btn bmx-btn-secondary"
          >
            acceder
          </a>
          <a href="/#contacto" className="bmx-btn">
            solicitar demo
          </a>
        </div>
      </nav>

      <header className="bmx-hero">
        <div className="bmx-hero-scene" aria-hidden="true">
          <span className="bmx-hero-layer bmx-hero-layer-grid" />
          <span className="bmx-hero-layer bmx-hero-layer-card" />
          <span className="bmx-hero-layer bmx-hero-layer-rail" />
          <div className="bmx-dashboard">
            <div className="bmx-dashboard-top">
              <div className="bmx-panel">
                <div className="bmx-panel-title">
                  <span>resumen diario</span>
                  <span>hoy</span>
                </div>
                <div className="bmx-metric-row">
                  <div className="bmx-metric">
                    <strong>28</strong>
                    <span>citas</span>
                  </div>
                  <div className="bmx-metric">
                    <strong>16</strong>
                    <span>atendidas</span>
                  </div>
                  <div className="bmx-metric">
                    <strong>4</strong>
                    <span>urgentes</span>
                  </div>
                </div>
              </div>

              <div className="bmx-panel">
                <div className="bmx-panel-title">
                  <span>actividad</span>
                  <span>semana</span>
                </div>
                <div className="bmx-chart">
                  <span style={{ height: 42 }} />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>

            <div className="bmx-panel">
              <div className="bmx-panel-title">
                <span>proximas atenciones</span>
                <span>consulta externa</span>
              </div>
              <div className="bmx-appointment-list">
                <div className="bmx-appointment">
                  <span>09:30</span>
                  <div>
                    <strong>Maria Perez</strong>
                    <span>Control general</span>
                  </div>
                  <span className="bmx-pill">confirmada</span>
                </div>
                <div className="bmx-appointment">
                  <span>10:15</span>
                  <div>
                    <strong>Carlos Mena</strong>
                    <span>Resultados</span>
                  </div>
                  <span className="bmx-pill">en espera</span>
                </div>
                <div className="bmx-appointment">
                  <span>11:00</span>
                  <div>
                    <strong>Ana Torres</strong>
                    <span>Primera visita</span>
                  </div>
                  <span className="bmx-pill">nuevo</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bmx-hero-content">
          <div className="bmx-eyebrow">sistema medico en la nube</div>
          <h1 className="bmx-title">
            AKLIO
          </h1>
          <p className="bmx-hero-copy">
            Gestiona pacientes, citas, historias clinicas, documentos medicos
            y reportes desde una plataforma creada para consultorios, equipos
            medicos y clinicas en crecimiento.
          </p>
          <div className="bmx-hero-actions">
            <a href="#planes" className="bmx-btn">
              ver planes
            </a>
            <a href="#modulos" className="bmx-btn bmx-btn-secondary">
              explorar modulos
            </a>
          </div>
        </div>
      </header>

      <section className="bmx-quick-strip" aria-label="Resumen de AKLIO">
        <div className="bmx-quick-item">
          <strong>24/7</strong>
          <span>acceso web para trabajar desde cualquier lugar</span>
        </div>
        <div className="bmx-quick-item">
          <strong>100%</strong>
          <span>expedientes digitales, ordenados y consultables</span>
        </div>
        <div className="bmx-quick-item">
          <strong>multi</strong>
          <span>usuarios, roles, especialidades y sedes</span>
        </div>
        <div className="bmx-quick-item">
          <strong>data</strong>
          <span>reportes para medir actividad y crecimiento</span>
        </div>
      </section>

      <section id="modulos" className="bmx-section">
        <div className="bmx-section-header">
          <div>
            <div className="bmx-section-label">modulos principales</div>
            <h2 className="bmx-section-title">Todo el flujo clinico en un solo lugar.</h2>
          </div>
          <p className="bmx-section-copy">
            AKLIO une la operacion diaria del centro medico con el registro
            clinico del paciente, para que cada cita avance con informacion
            clara, trazable y disponible cuando se necesita.
          </p>
        </div>

        <div className="bmx-modules-grid">
          {modules.map((module, index) => (
            <article className="bmx-module" key={module.title}>
              <span className="bmx-module-num">{String(index + 1).padStart(2, '0')}</span>
              <h3>{module.title}</h3>
              <p>{module.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="beneficios" className="bmx-section bmx-benefits">
        <div className="bmx-benefit-grid">
          <div>
            <div className="bmx-section-label">beneficios</div>
            <h2 className="bmx-section-title">Menos desorden. Mas control medico.</h2>
            <p className="bmx-section-copy">
              El valor de AKLIO esta en simplificar la rutina del equipo:
              informacion accesible, procesos consistentes y una experiencia
              mas fluida para el paciente.
            </p>
          </div>

          <div className="bmx-benefit-list">
            {benefits.map((benefit, index) => (
              <article className="bmx-benefit" key={benefit.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <div>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bmx-section">
        <div className="bmx-section-header">
          <div>
            <div className="bmx-section-label">expediente digital</div>
            <h2 className="bmx-section-title">Consulta con historial claro.</h2>
          </div>
          <p className="bmx-section-copy">
            Cada atencion puede guardar antecedentes, notas, diagnosticos,
            evolucion y documentos relacionados. El equipo ve el contexto del
            paciente sin buscar entre archivos sueltos.
          </p>
        </div>

        <div className="bmx-record-preview" aria-hidden="true">
          <div className="bmx-record-header">
            <strong>Historia clinica - Paciente</strong>
            <span className="bmx-pill">expediente activo</span>
          </div>
          <div className="bmx-record-body">
            <div className="bmx-timeline">
              <div className="bmx-timeline-item">
                <strong>Antecedentes</strong>
                <span>familiares, personales, alergias</span>
              </div>
              <div className="bmx-timeline-item">
                <strong>Consulta actual</strong>
                <span>motivo, signos vitales, diagnostico</span>
              </div>
              <div className="bmx-timeline-item">
                <strong>Documentos</strong>
                <span>recetas, certificados, indicaciones</span>
              </div>
            </div>
            <div className="bmx-notes">
              <div className="bmx-note-line" />
              <div className="bmx-note-line" />
              <div className="bmx-note-line" />
              <div className="bmx-note-line" />
              <div className="bmx-badge-row">
                <span className="bmx-pill">diagnostico</span>
                <span className="bmx-pill">tratamiento</span>
                <span className="bmx-pill">seguimiento</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="planes" className="bmx-section bmx-dark-section">
        <div className="bmx-section-header">
          <div>
            <div className="bmx-section-label">planes</div>
            <h2 className="bmx-section-title">Elige el alcance que necesita tu operacion.</h2>
          </div>
          <p className="bmx-section-copy">
            Elige entre planes para consultorio privado o una propuesta escalable
            para clinicas. Consultorio privado se muestra por defecto y puedes
            cambiar el grupo pasando el cursor o tocando cada opcion.
          </p>
        </div>

        <div className="bmx-plan-audience" aria-label="Tipos de planes AKLIO">
          <button
            type="button"
            className={activePlanGroup === 'consultorio' ? 'bmx-plan-audience-active' : ''}
            onMouseEnter={() => setActivePlanGroup('consultorio')}
            onFocus={() => setActivePlanGroup('consultorio')}
            onClick={() => setActivePlanGroup('consultorio')}
            aria-pressed={activePlanGroup === 'consultorio'}
          >
            Consultorio privado
          </button>
          <button
            type="button"
            className={activePlanGroup === 'clinicas' ? 'bmx-plan-audience-active' : ''}
            onMouseEnter={() => setActivePlanGroup('clinicas')}
            onFocus={() => setActivePlanGroup('clinicas')}
            onClick={() => setActivePlanGroup('clinicas')}
            aria-pressed={activePlanGroup === 'clinicas'}
          >
            Clinicas
          </button>
        </div>

        <div className={`bmx-plan-grid${activePlanGroup === 'clinicas' ? ' bmx-plan-grid-clinicas' : ''}`}>
          {visiblePlans.map((plan) => (
            <article
              className={`bmx-plan${plan.featured ? ' bmx-plan-featured' : ''}`}
              key={plan.name}
            >
              <span className="bmx-plan-kicker">{plan.kicker}</span>
              <h3>{plan.name}</h3>
              <div className="bmx-plan-price">
                {plan.price}
                {plan.priceNote && <span>{plan.priceNote}</span>}
              </div>
              <p className="bmx-plan-desc">{plan.description}</p>
              <ul>
                {plan.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              {plan.note && (
                <span className="bmx-plan-note">{plan.note}</span>
              )}
              <a href="/#contacto" className="bmx-link-btn">
                solicitar cotizacion
              </a>
            </article>
          ))}
        </div>
      </section>

      <section className="bmx-section bmx-legal-band">
        <div className="bmx-section-header">
          <div>
            <div className="bmx-section-label">documentos legales</div>
            <h2 className="bmx-section-title">Informacion clara para usar AKLIO.</h2>
          </div>
          <p className="bmx-section-copy">
            Revisa los terminos del servicio, la politica de privacidad, el uso
            de cookies y el acuerdo de procesamiento de datos aplicable a la
            plataforma.
          </p>
        </div>

        <div className="bmx-legal-grid">
          {aklioLegalDocuments.map((document, index) => (
            <Link
              className="bmx-legal-card"
              to={`/aklio/legal/${document.slug}`}
              key={document.slug}
            >
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h3>{document.navLabel}</h3>
              <p>{document.description}</p>
              <strong>ver documento</strong>
            </Link>
          ))}
        </div>
      </section>

      <section className="bmx-section bmx-cta">
        <div>
          <h2>Agenda una demo de AKLIO.</h2>
          <p>
            Revisamos contigo el flujo actual de tu consultorio o clinica y te
            mostramos como AKLIO puede organizarlo en una plataforma unica.
          </p>
        </div>
        <a href="/#contacto" className="bmx-btn">
          hablar con thebluecoding
        </a>
      </section>

      <footer className="bmx-footer">
        <Link to="/" className="bmx-logo">
          the<span>blue</span>coding
        </Link>
        <span>AKLIO - sistema medico digital</span>
      </footer>
    </div>
  );
};

export default AKLIOPage;
